import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ChevronRight, Map, Search, Target, Navigation } from 'lucide-react';
export function DiscoveryRoutingPage() {
  return <div className="pt-32 pb-20" data-id="element-6095">
      {/* Hero */}
      <section className="max-w-4xl mx-auto px-6 text-center mb-24" data-id="element-6096">
        <motion.h1 initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="text-5xl md:text-6xl font-bold tracking-tight mb-6 leading-tight" data-id="element-6097">
          Discovery that's built for booking — not just browsing.
        </motion.h1>
        <motion.p initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.1
      }} className="text-xl text-textMuted mb-10 max-w-3xl mx-auto leading-relaxed" data-id="element-6098">
          Search venues or artists by fit, geography, capacity, budget, and
          route context, then turn that discovery into real booking workflow.
        </motion.p>
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.2
      }} data-id="element-6099">
          <Link to="/onboarding" className="inline-flex items-center gap-2 px-8 py-4 text-base font-medium text-background bg-primary rounded-lg hover:bg-primaryHover transition-all shadow-[0_0_20px_rgba(245,158,11,0.3)]" data-id="element-6100">
            See Booker in action
            <ChevronRight className="w-4 h-4" data-id="element-6101" />
          </Link>
        </motion.div>
      </section>

      {/* Features Grid */}
      <section className="max-w-6xl mx-auto px-6 mb-24" data-id="element-6102">
        <div className="grid md:grid-cols-2 gap-12 items-center mb-24" data-id="element-6103">
          <div data-id="element-6104">
            <div className="w-12 h-12 rounded-lg bg-primary/10 text-primary flex items-center justify-center mb-6" data-id="element-6105">
              <Navigation className="w-6 h-6" data-id="element-6106" />
            </div>
            <h2 className="text-3xl font-bold mb-4" data-id="element-6107">Route-aware matching</h2>
            <p className="text-lg text-textMuted leading-relaxed mb-6" data-id="element-6108">
              Don't just find a venue—find a venue that makes sense for your
              route. Booker calculates drive times, suggests logical anchor
              dates, and highlights gaps in your itinerary.
            </p>
            <ul className="space-y-3" data-id="element-6109">
              <li className="flex items-center gap-3 text-textMuted" data-id="element-6110">
                <div className="w-1.5 h-1.5 rounded-full bg-primary" data-id="element-6111"></div>{' '}
                Drive time optimization
              </li>
              <li className="flex items-center gap-3 text-textMuted" data-id="element-6112">
                <div className="w-1.5 h-1.5 rounded-full bg-primary" data-id="element-6113"></div>{' '}
                Anchor date suggestions
              </li>
              <li className="flex items-center gap-3 text-textMuted" data-id="element-6114">
                <div className="w-1.5 h-1.5 rounded-full bg-primary" data-id="element-6115"></div>{' '}
                Route gap identification
              </li>
            </ul>
          </div>
          <div className="bg-surface border border-border rounded-xl p-6 h-80 relative overflow-hidden" data-id="element-6116">
            {/* Mockup visual */}
            <div className="absolute inset-0 bg-[radial-gradient(#27272A_1px,transparent_1px)] [background-size:16px_16px] opacity-20" data-id="element-6117"></div>
            <div className="absolute top-1/4 left-1/4 w-4 h-4 rounded-full bg-primary shadow-[0_0_10px_rgba(245,158,11,0.5)]" data-id="element-6118"></div>
            <div className="absolute top-1/2 left-1/2 w-4 h-4 rounded-full bg-primary shadow-[0_0_10px_rgba(245,158,11,0.5)]" data-id="element-6119"></div>
            <div className="absolute top-1/3 right-1/4 w-4 h-4 rounded-full border-2 border-primary bg-background" data-id="element-6120"></div>
            <svg className="absolute inset-0 w-full h-full pointer-events-none" data-id="element-6121">
              <path d="M 25% 25% L 50% 50% L 75% 33%" fill="none" stroke="#F59E0B" strokeWidth="2" strokeDasharray="4 4" className="opacity-50" data-id="element-6122" />
            </svg>
            <div className="absolute bottom-6 left-6 right-6 bg-background border border-border rounded-lg p-4 shadow-lg" data-id="element-6123">
              <div className="text-sm font-medium mb-1" data-id="element-6124">Route Gap Detected</div>
              <div className="text-xs text-textMuted" data-id="element-6125">
                420 miles between Chicago and Buffalo. Suggest adding a stop in
                Cleveland or Erie.
              </div>
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-12 items-center md:flex-row-reverse" data-id="element-6126">
          <div className="md:order-2" data-id="element-6127">
            <div className="w-12 h-12 rounded-lg bg-primary/10 text-primary flex items-center justify-center mb-6" data-id="element-6128">
              <Target className="w-6 h-6" data-id="element-6129" />
            </div>
            <h2 className="text-3xl font-bold mb-4" data-id="element-6130">
              Radius clause intelligence
            </h2>
            <p className="text-lg text-textMuted leading-relaxed mb-6" data-id="element-6131">
              Booker automatically flags potential radius clause violations
              before you send an offer, saving you from embarrassing
              double-bookings or contract breaches.
            </p>
            <ul className="space-y-3" data-id="element-6132">
              <li className="flex items-center gap-3 text-textMuted" data-id="element-6133">
                <div className="w-1.5 h-1.5 rounded-full bg-primary" data-id="element-6134"></div>{' '}
                Automatic distance calculation
              </li>
              <li className="flex items-center gap-3 text-textMuted" data-id="element-6135">
                <div className="w-1.5 h-1.5 rounded-full bg-primary" data-id="element-6136"></div>{' '}
                Timeframe conflict detection
              </li>
              <li className="flex items-center gap-3 text-textMuted" data-id="element-6137">
                <div className="w-1.5 h-1.5 rounded-full bg-primary" data-id="element-6138"></div>{' '}
                Festival exclusivity warnings
              </li>
            </ul>
          </div>
          <div className="bg-surface border border-border rounded-xl p-6 h-80 md:order-1 flex flex-col justify-center" data-id="element-6139">
            <div className="bg-background border border-border rounded-lg p-4 mb-4 opacity-50" data-id="element-6140">
              <div className="flex justify-between items-center mb-2" data-id="element-6141">
                <div className="font-medium text-sm" data-id="element-6142">The Sinclair</div>
                <div className="text-xs text-textMuted" data-id="element-6143">Jun 18</div>
              </div>
            </div>
            <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4 relative z-10 scale-105 shadow-xl" data-id="element-6144">
              <div className="flex justify-between items-center mb-2" data-id="element-6145">
                <div className="font-medium text-sm text-amber-500" data-id="element-6146">
                  Brighton Music Hall
                </div>
                <div className="text-xs text-amber-500" data-id="element-6147">Jun 20</div>
              </div>
              <div className="text-xs text-amber-500/80 mt-2 pt-2 border-t border-amber-500/20" data-id="element-6148">
                Warning: Violates 60-mile / 30-day radius clause with The
                Sinclair.
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>;
}